import { v4 as uuidv4 } from "uuid"
import { HttpRequest } from "../utils"

const httpRequest = new HttpRequest()

// §26 — create / sender-verify / receiver-verify wrap in `withIdempotency` on
// the BE. A fresh uuid v4 per call lets a network retry replay safely
// (non-strict: a missing key just skips dedup, never 400s).
const idem = (): Record<string, string> => ({ "Idempotency-Key": uuidv4() })

export interface ICustodyExpectedItem {
  sku?: string
  productId?: string
  name?: string
  unit?: string
  quantity: number
  // Product thumbnail URL — enriched at read time by the BE (custody item
  // snapshots are stored without it). Absent when the variant has no image.
  image?: string | null
}

export type CustodyItemCondition = "GOOD" | "DAMAGED" | "MISSING"

// Sender-declared released item — expected-item shape plus an optional
// per-item condition. Advisory: the receiver remains authoritative for stock.
export interface ICustodyReleasedItem extends ICustodyExpectedItem {
  condition?: CustodyItemCondition
}

export interface ICustodyUserSummary {
  id: string
  firstName: string | null
  lastName: string | null
  phone: string | null
}

export interface ICustodyDeliveryNoteSummary {
  id: string
  refCode: string | null
  receiverBusiness: { refCode: string | null; profileName: string | null } | null
}

export interface ICustodyRouteSummary {
  id: string
  routeNumber: string
  name: string | null
  stopNumber: number
}

export interface ICustodyTransfer {
  id: string
  businessId: string
  deliveryNoteId: string | null
  transferType: string
  status: string
  fromUserId: string
  toUserId: string
  expectedItems: ICustodyExpectedItem[]
  receivedItems: ICustodyExpectedItem[] | null
  discrepancies: Array<{ key: string; expected: number; received: number }> | null
  // Sender-side reconciliation (advisory). Populated at sender-verify when the
  // sender declares per-item released quantities; null for PIN-only handovers.
  releasedItems?: ICustodyReleasedItem[] | null
  senderDiscrepancies?: Array<{ key: string; expected: number; released: number }> | null
  initiatedAt: string | null
  senderVerifiedAt: string | null
  receiverVerifiedAt: string | null
  completedAt: string | null
  cancelledAt: string | null
  cancelledReason: string | null
  /** BE contract §2 (O211): set when the system cancelled a duplicate; then `cancelledReason` is not shown. */
  cancelledReasonCode?: "DUPLICATE_SUPERSEDED" | null
  createdByUserId: string
  createdAt?: string
  updatedAt?: string
  // Enriched fields — populated by GET /custody/transfers/:id AND by the list
  // endpoint (GET /custody/transfers), which batch-resolves users/DN/route.
  fromUser?: ICustodyUserSummary | null
  toUser?: ICustodyUserSummary | null
  deliveryNote?: ICustodyDeliveryNoteSummary | null
  // Route info resolved via delivery_route_stop. Lets the handover UI
  // identify which route this transfer belongs to ("Хан-Уул хүргэлт")
  // instead of falling back to the opaque transfer id.
  route?: ICustodyRouteSummary | null
  // "{district} хүргэлт" derived from the DN's destination branch. Set even
  // when the DN was never added to a formal route, so the handover card stays
  // recognisable instead of showing the opaque HS- id.
  deliveryLabel?: string | null
}

export interface ICreateCustodyTransferInput {
  deliveryNoteId?: string | null
  transferType: string
  fromUserId: string
  toUserId: string
  expectedItems: ICustodyExpectedItem[]
}

export const create = async (data: ICreateCustodyTransferInput): Promise<ICustodyTransfer> => {
  return httpRequest.post("/ord/custody/transfers", data as any, idem())
}

export const get = async (id: string): Promise<ICustodyTransfer> => {
  return httpRequest.get(`/ord/custody/transfers/${id}`)
}

// O08: a retried initiate replays instead of failing on the second DRAFT check.
export const initiate = async (id: string): Promise<ICustodyTransfer> => {
  return httpRequest.post(`/ord/custody/transfers/${id}/initiate`, {} as any, idem())
}

export const senderVerify = async (
  id: string,
  body: { pin: string; releasedItems?: ICustodyReleasedItem[] },
): Promise<ICustodyTransfer> => {
  return httpRequest.post(`/ord/custody/transfers/${id}/sender-verify`, body as any, idem())
}

export const receiverVerify = async (
  id: string,
  body: { pin: string; receivedItems: ICustodyExpectedItem[] },
): Promise<ICustodyTransfer> => {
  return httpRequest.post(`/ord/custody/transfers/${id}/receiver-verify`, body as any, idem())
}

// Spec §2.5 — handover log embed.
export const listByDn = async (deliveryNoteId: string): Promise<{ rows: ICustodyTransfer[]; count: number }> => {
  return httpRequest.get(`/ord/custody/transfers`, { deliveryNoteId })
}

// Spec §2.5 Tab 3 — Хүлээлцэх list view. Backend requires at least one
// of status / transferType / *UserId — passing a status filter satisfies it.
export const list = async (params: {
  status?: string[]
  transferType?: string[]
  routeId?: string
}): Promise<{ rows: ICustodyTransfer[]; count: number }> => {
  const query: Record<string, string> = {}
  if (params.status && params.status.length > 0) query.status = params.status.join(",")
  if (params.transferType && params.transferType.length > 0) query.transferType = params.transferType.join(",")
  if (params.routeId) query.routeId = params.routeId
  return httpRequest.get(`/ord/custody/transfers`, query)
}
